import { create } from "zustand";

import { Benefit, Plan, SEGMENTOS_POS } from "@/api/products";
import { RequestValue } from "@/api/options";
import { getFidelityByDDD, getFidelityValueByDDD, getPriceBaseByDDD } from "@/utils";

type ProductStatus = "valid" | "invalid";

export type ProductStore = {
  item: Plan | null;
  areaCode: string;
  setAreaCode(areaCode: string): void;
  setProduct(product: Plan): void;
  removeBonusPortability(): void;
  addBonusPortability(): void;
  isExpress(): boolean;
  isPos(): boolean;
  shouldAddSpecialBonus(): boolean;
  clienteQuerDescontoExtra(): boolean;

  payment: RequestValue | null;
  setPayment(payment: RequestValue): void;

  loyalty: boolean;
  setLoyalty(value: boolean): void;

  status: ProductStatus;
  setStatus(value: ProductStatus): void;

  getCurrentPrice(ddd?: string): number;
  clearProductStores(): void;

  getTotalGbProduct(): string;
};

export const useProductStore = create<ProductStore>((set, get) => ({
  item: null,
  areaCode: "",
  setAreaCode(areaCode: string) {
    set((state) => ({ ...state, areaCode }));
  },
  setProduct(product: Plan) {
    set((state) => ({ ...state, item: product }));
  },
  isPos() {
    const segmento = get().item?.segmento;

    return segmento ? SEGMENTOS_POS.includes(segmento) : false;
  },
  removeBonusPortability() {
    set((state) => {
      // Sem plano nao ha o que recalcular; espalhar null aqui criaria um
      // item fantasma sem beneficios/precos que quebra getCurrentPrice.
      if (!state.item) {
        return state;
      }

      const bonusDedicado = state?.item?.mostrarDedicado;
      const gbBonusDedicado = bonusDedicado ? state?.item?.gbBonusDedicated : 0;

      const newGB =
        (state?.item?.gbPadrao || 0) +
        (state?.item?.gbPromocional || 0) +
        (gbBonusDedicado || 0);

      const newItem = {
        ...state.item,
        gbBonusPortabilidade: 0,
        gb: newGB,
      } as Plan;

      return { ...state, item: newItem };
    });
  },
  addBonusPortability() {
    set((state) => {
      if (!state.item) {
        return state;
      }

      const bonusPortabilidade = state?.item?.bonusPortabilidade;
      const gbBonusPortabilidade = bonusPortabilidade;

      const bonusDedicado = state?.item?.mostrarDedicado;
      const gbBonusDedicado = bonusDedicado ? state?.item?.gbBonusDedicated : 0;

      const newGB =
        (state?.item?.gbPadrao || 0) +
        (state?.item?.gbPromocional || 0) +
        (gbBonusDedicado || 0) +
        (gbBonusPortabilidade || 0);

      const newItem = {
        ...state.item,
        ...{
          gbBonusPortabilidade,
          gb: newGB,
          mostrarPortabilidade: true,
        },
      } as Plan;
      return { ...state, item: newItem };
    });
  },

  isExpress() {
    const segmento = get().item?.segmento;

    return segmento === "EXPRESS" || segmento === "POS_EXPRESS";
  },

  shouldAddSpecialBonus() {
    return get().item?.mostrarPortabilidade ?? false;
  },

  clienteQuerDescontoExtra() {
    // const segmento = get().item?.segmento;
    //
    // return segmento === 'CONTROLE' || segmento === 'EXPRESS';
    //
    return false;
  },

  payment: null,
  setPayment(payment: RequestValue) {
    set((state) => ({ ...state, payment }));
  },

  loyalty: true,
  setLoyalty(value: boolean) {
    set((state) => ({ ...state, loyalty: value }));
  },

  status: "valid",
  setStatus(value) {
    set((state) => ({ ...state, status: value }));
  },

  getCurrentPrice(ddd?: string) {
    ddd = ddd || get().areaCode || undefined;

    const temDescontoExtra = this.item?.beneficios.some(
      ({ componente } : Benefit) => componente === "DescontoExtra"
    );

    const valorDescontoExtra = temDescontoExtra && this.clienteQuerDescontoExtra() ? 5 : 0;

    if (ddd && String(ddd).length === 2 && this.item?.fidelidades) {
      const fidelity = getFidelityByDDD(this.item.fidelidades, String(ddd));
      const defaultPrice = getPriceBaseByDDD(this.item.precos, String(ddd));

      const loyaltyDiscount = fidelity
        ? getFidelityValueByDDD(fidelity, String(ddd))
        : 0;

      return (
        Math.round(
          (defaultPrice! + (this.loyalty ? loyaltyDiscount : 0) - valorDescontoExtra) * 100
        ) / 100
      );
    }

    const defaultPrice = getPriceBaseByDDD(
      this.item?.precos,
      import.meta.env.VITE_DDD_DEFAULT_PLANS
    );

    const loyaltyDiscount = Number(
      this.item?.fidelidades
        ?.find((f) =>
          f.precos.some((p) => p.ddd === import.meta.env.VITE_DDD_DEFAULT_PLANS)
        )
        ?.precos.find((p) => p.ddd === import.meta.env.VITE_DDD_DEFAULT_PLANS)
        ?.preco_atual
    );

    if (loyaltyDiscount && this.loyalty) {
      return Math.round((defaultPrice! + loyaltyDiscount - valorDescontoExtra) * 100) / 100;
    }
    return defaultPrice;
  },
  clearProductStores() {
    set((state) => ({
      ...state,
      item: null,
      status: "valid",
      loyalty: true,
      payment: null,
    }));
  },
  getTotalGbProduct() {
    const plan = this.item;

    const gbPlan = `${plan?.gbPadrao}gb`;

    const gbPortability = plan?.mostrarPortabilidade
      ? `+${plan.gbBonusPortabilidade}gb`
      : "";

    const gbDedicado = plan?.mostrarDedicado
      ? `+${plan.gbBonusDedicated}gb`
      : "";

    const gbPromocional = plan?.gbPromocional ? `+${plan.gbPromocional}gb` : "";

    return gbPlan + gbPortability + gbPromocional + gbDedicado;
  },
}));
